import React, { useState, useEffect, useMemo } from 'react';
import { Container, Typography, Box, Fade, Button, IconButton, Tooltip, Divider, useMediaQuery } from '@mui/material';
import { useHistory } from 'react-router-dom';
import LoadingAnimation from './LoadingAnimation';
import { useTenantTheme } from '../tenant/TenantThemeProvider';

import DanosDetalle from './DamageDetalle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
// Importar iconos adicionales para las funcionalidades de zoom
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';

const AnalisisGeneral = ({ resultados: resultadosProp, onResetAnalisis, onResetInspeccion }) => {
  const { brandColors: tenantBrandColors } = useTenantTheme();
  const brandColors = useMemo(() => ({
    ...tenantBrandColors
  }), [tenantBrandColors]);

  const [loading, setLoading] = useState(true);
  const [expandedImage, setExpandedImage] = useState(null);
  const [showDanos, setShowDanos] = useState(false);
  const history = useHistory();
  const [resultados, setResultados] = useState(null);
  const [copiedField, setCopiedField] = useState('');
  const isMobile = useMediaQuery('(max-width:900px)');



  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(''), 2000);
  };

  useEffect(() => {
    if (resultados) {
      const timer = setTimeout(() => setLoading(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [resultados]);

  useEffect(() => {
    if (resultadosProp) {
      setResultados(resultadosProp);
      setLoading(false);
    } else {
      const savedResults = localStorage.getItem('resultadosResumen') || localStorage.getItem('imagenesProcessadas');
      if (savedResults) {
        const parsedResults = JSON.parse(savedResults);
        // Recuperar las imágenes completas del localStorage
        for (let i = 0; i < 11; i++) {
          const fullImage = localStorage.getItem(`photo_${i}`);
          if (fullImage) {
            if (i < 4 && parsedResults.danos && parsedResults.danos[i]) {
              parsedResults.danos[i].imagen = fullImage;
            } else if (i === 7 && parsedResults.odometro) {
              parsedResults.odometro.imagen = fullImage;
            } else if (i === 9 && parsedResults.chasis) {
              parsedResults.chasis.imagen = fullImage;
            } else if (i === 10 && parsedResults.documento) {
              parsedResults.documento.imagen = fullImage;
            }
          }
        }
        setResultados(parsedResults);
        setLoading(false);
      } else {
        history.push('/process-images');
      }
    }
  }, [resultadosProp, history]);

  if (loading) {
    return <LoadingAnimation />;
  }

  if (showDanos) {
    return <DanosDetalle 
      danos={resultados.danos} 
      onBack={() => setShowDanos(false)} 
      resultados={resultados}
      onResetInspeccion={onResetInspeccion}
    />;
  }

  if (!resultados) {
    return <Typography>No se encontraron resultados. Por favor, vuelva a procesar las imágenes.</Typography>;
  }

  const totalDanos = Array.isArray(resultados.danos)
    ? resultados.danos.reduce((acc, item) => {
      const directDamages = Array.isArray(item?.all_damages) ? item.all_damages.length : 0;
      const partDamages = Array.isArray(item?.part_damages)
        ? item.part_damages.reduce((sum, partDamage) => sum + ((partDamage?.associated_damages || []).length), 0)
        : 0;
      const fallbackDamages = Array.isArray(item?.danos) ? item.danos.length : 0;

      return acc + Math.max(directDamages, partDamages, fallbackDamages);
    }, 0)
    : 0;

  const totalPiezasAfectadas = Array.isArray(resultados.danos)
    ? resultados.danos.reduce((acc, item) => acc + (Array.isArray(item?.part_damages) ? item.part_damages.length : 0), 0)
    : 0;

  const formatVisualizationUrl = (url) => {
    if (!url || typeof url !== 'string') {
      console.error('No hay URL de visualización o no es string:', url);
      return '';
    }
    
    if (url.startsWith('http')) {
      console.log('URL de visualización es una dirección web:', url.substring(0, 30) + '...');
      return url;
    }
    
    // Si ya tiene el prefijo data:image, dejarlo como está
    if (url.startsWith('data:image')) {
      console.log('URL ya está en formato data:image');
      return url;
    }
    
    // Convertir a formato base64 para imagen
    console.log('Añadiendo prefijo data:image/jpeg;base64, a la URL');
    return `data:image/jpeg;base64,${url}`;
  };



  const ResultBox = ({ title, children, sx = {} }) => (
    <Box 
      className="result-box" 
      p={3} 
      borderRadius={2} 
      sx={{
        backgroundColor: brandColors.cardBackground,
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
        marginBottom: 4,
        borderLeft: `4px solid ${brandColors.primary}`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          transform: 'translateY(-2px)'
        },
        ...sx
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ 
          color: brandColors.text,
          marginBottom: '20px', 
          fontWeight: 'bold',
          position: 'relative',
          paddingBottom: '8px',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '40px',
            height: '3px',
            backgroundColor: brandColors.primary,
            borderRadius: '1px'
          }
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );

  const NoDetectionMessage = ({ message }) => (
    <Typography 
      variant="body1" 
      style={{ 
        color: `${brandColors.text}CC`,
        backgroundColor: `${brandColors.primary}14`,
        padding: '10px', 
        borderRadius: '4px',
        textAlign: 'center'
      }}
    >
      {message}
    </Typography>
  );

  const ImageModal = ({ image, onClose }) => {
    const [localZoomLevel, setLocalZoomLevel] = useState(1);
    const [localPanPosition, setLocalPanPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    
    // Restablecer zoom y posición al cerrar
    const handleClose = () => {
      onClose();
      setLocalZoomLevel(1);
      setLocalPanPosition({ x: 0, y: 0 });
    };

    const handleZoomIn = (e) => {
      e.stopPropagation();
      if (localZoomLevel < 3) { // Limitar nivel máximo de zoom
        setLocalZoomLevel(prev => prev + 0.25);
      }
    };

    const handleZoomOut = (e) => {
      e.stopPropagation();
      if (localZoomLevel > 1) { // No permitir zoom menor que 1
        setLocalZoomLevel(prev => prev - 0.25);
        // Si estamos haciendo zoom out, reducir también la posición de pan proporcionalmente
        setLocalPanPosition(prev => ({
          x: prev.x / 1.25,
          y: prev.y / 1.25
        }));
      } else {
        // Si llegamos a nivel 1, resetear la posición
        setLocalPanPosition({ x: 0, y: 0 });
      }
    };
    
    // Para manejar el gesto de pinch-zoom en móviles
    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.stopPropagation();
        // Calcular la distancia inicial entre los dos dedos
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        // Guardar la distancia como un atributo del elemento para usarlo en touchmove
        e.currentTarget.touchStartDistance = distance;
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2 && e.currentTarget.touchStartDistance) {
        e.stopPropagation();
        // Calcular la nueva distancia entre los dedos
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const newDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        
        // Calcular el cambio proporcional en la distancia
        const ratio = newDistance / e.currentTarget.touchStartDistance;
        
        // Actualizar el zoom basado en el ratio, pero con límites
        let newZoom = localZoomLevel * ratio;
        if (newZoom < 1) newZoom = 1;
        if (newZoom > 3) newZoom = 3;
        
        setLocalZoomLevel(newZoom);
        e.currentTarget.touchStartDistance = newDistance; // Actualizar la distancia de referencia
      } else if (e.touches.length === 1 && localZoomLevel > 1) {
        // Si solo hay un dedo y tenemos zoom, permitir mover la imagen
        if (!isDragging) {
          setIsDragging(true);
          setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
        } else {
          const deltaX = e.touches[0].clientX - dragStart.x;
          const deltaY = e.touches[0].clientY - dragStart.y;
          setLocalPanPosition(prev => ({
            x: prev.x + deltaX,
            y: prev.y + deltaY
          }));
          setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
        }
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    // Para arrastrar la imagen con el mouse
    const handleMouseDown = (e) => {
      if (localZoomLevel > 1) {
        e.stopPropagation();
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMouseMove = (e) => {
      if (isDragging && localZoomLevel > 1) {
        e.stopPropagation();
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        setLocalPanPosition(prev => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY
        }));
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    // Resetear zoom y posición
    const handleReset = (e) => {
      e.stopPropagation();
      setLocalZoomLevel(1);
      setLocalPanPosition({ x: 0, y: 0 });
    };

    // Estilo del contenedor principal
    const modalStyle = {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 2000,
      touchAction: localZoomLevel > 1 ? 'none' : 'auto', // Evitar gestos del navegador cuando hay zoom
    };

    return (
      <Box
        sx={modalStyle}
        onClick={handleClose}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleTouchEnd}
      >
        <Box 
          sx={{
            position: 'relative',
            width: '95%',
            height: '95%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={image}
            alt="Expanded view"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              transform: `scale(${localZoomLevel}) translate(${localPanPosition.x / localZoomLevel}px, ${localPanPosition.y / localZoomLevel}px)`,
              transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              cursor: localZoomLevel > 1 ? 'grab' : 'default',
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
          />
          
          {/* Controles de zoom */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              borderRadius: '20px',
              padding: '8px 16px',
              zIndex: 2010,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton 
              onClick={handleZoomOut} 
              size="small" 
              disabled={localZoomLevel <= 1}
              sx={{ color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
            >
              <ZoomOutIcon />
            </IconButton>
            
            <Typography variant="body2" sx={{ color: 'white', margin: '0 8px', alignSelf: 'center' }}>
              {Math.round(localZoomLevel * 100)}%
            </Typography>
            
            <IconButton 
              onClick={handleZoomIn} 
              size="small"
              disabled={localZoomLevel >= 3}
              sx={{ color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
            >
              <ZoomInIcon />
            </IconButton>
            
            <Divider orientation="vertical" flexItem sx={{ margin: '0 8px', backgroundColor: 'rgba(255,255,255,0.3)' }} />
            
            <IconButton 
              onClick={handleReset} 
              size="small"
              disabled={localZoomLevel === 1 && localPanPosition.x === 0 && localPanPosition.y === 0}
              sx={{ color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
            >
              <FullscreenIcon />
            </IconButton>
          </Box>
          
          {/* Botón de cerrar */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderDocumentoData = (documentoData) => {
    if (!documentoData || typeof documentoData !== 'object') {
      return <NoDetectionMessage message="No se pudieron extraer datos del documento." />;
    }

    const dataFuente = documentoData.extracted_data && typeof documentoData.extracted_data === 'object'
      ? documentoData.extracted_data
      : documentoData;

    const allowedFields = [
      { key: 'Marca', label: 'Marca' },
      { key: 'Modelo', label: 'Modelo' },
      { key: 'Patente', label: 'Patente' },
      { key: 'Año', label: 'Año' },
      { key: 'Numero_chasis', label: 'Número chasis' },
      { key: 'Numero_motor', label: 'Número motor' },
      { key: 'Rut_adquiriente', label: 'RUT adquiriente' },
      { key: 'Nombre_adquiriente', label: 'Nombre adquiriente' },
      { key: 'Color', label: 'Color' },
      { key: 'Transmision', label: 'Transmisión' }
    ];

    const extractedData = allowedFields.reduce((acc, field) => {
      const value = dataFuente[field.key];
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        acc.push({ label: field.label, value: String(value), key: field.key });
      }
      return acc;
    }, []);

    if (extractedData.length === 0) {
      return <NoDetectionMessage message="No se extrajo información relevante del documento." />;
    }

    // Renderizar los datos extraídos de forma limpia
    return (
      <Box>
        {extractedData.map((item) => (
          <Box key={item.key} display="flex" justifyContent="space-between" alignItems="center" my={1} py={1} borderBottom="1px solid #eee">
            <Typography variant="body1" style={{ fontWeight: 'bold', color: '#333', textTransform: 'capitalize' }}>
              {item.label}:
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography variant="body1" style={{ color: '#555', marginRight: '8px' }}>
                {item.value}
              </Typography>
              <Tooltip title={copiedField === item.key ? "¡Copiado!" : "Copiar"}>
                <IconButton onClick={() => copyToClipboard(String(item.value), item.key)} size="small">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Fade in={!loading}>
      <Container className="container" style={{ backgroundColor: brandColors.background, minHeight: '100vh', padding: isMobile ? '14px' : '20px' }}>
        {/* Elemento decorativo superior */}
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '80px',
            height: '80px',
            background: brandColors.accent,
            borderRadius: '0 0 0 100%',
            zIndex: 1
          }}
        />
        
        {/* Elemento decorativo inferior */}
        <Box 
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '120px',
            height: '120px',
            background: `${brandColors.primary}33`,
            borderRadius: '0 100% 0 0',
            zIndex: 1
          }}
        />
        
        <Box textAlign="center" mb={isMobile ? 2.2 : 4} sx={{ position: 'relative', zIndex: 2 }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold',
            color: brandColors.text,
            position: 'relative',
            marginTop: isMobile ? '8px' : '20px',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60px',
              height: '4px',
              backgroundColor: brandColors.primary,
              borderRadius: '2px'
            }
          }}>
            Análisis General
          </Typography>
        </Box>
        <Box
          sx={{
            px: { xs: 0.5, md: 3 },
            mb: isMobile ? 2.2 : 2.8,
            position: 'relative',
            zIndex: 2
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              p: isMobile ? 1.4 : 1.8,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
              color: '#fff',
              boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
              flexDirection: isMobile ? 'column' : 'row',
              textAlign: isMobile ? 'center' : 'left'
            }}
          >
            <Box>
              <Typography sx={{ fontSize: isMobile ? '0.78rem' : '0.84rem', opacity: 0.85, fontWeight: 700, letterSpacing: 0.4 }}>
                Resumen de daños
              </Typography>
              <Typography sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 800, lineHeight: 1.1, mt: 0.4 }}>
                {totalDanos} {totalDanos === 1 ? 'daño detectado' : 'daños detectados'}
              </Typography>
              <Typography sx={{ fontSize: isMobile ? '0.76rem' : '0.88rem', opacity: 0.9, mt: 0.4 }}>
                {totalPiezasAfectadas} {totalPiezasAfectadas === 1 ? 'pieza afectada' : 'piezas afectadas'}. Revisa documento, tablero y chasis aquí. El detalle por pieza está en la vista de daños.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => setShowDanos(true)}
              sx={{
                minWidth: isMobile ? '100%' : 220,
                bgcolor: '#fff',
                color: brandColors.primary,
                fontWeight: 800,
                borderRadius: 999,
                px: 3,
                py: 1.1,
                '&:hover': { bgcolor: '#F8FAFC' }
              }}
            >
              Ver daños detectados
            </Button>
          </Box>
        </Box>
        <Box 
          sx={{ 
            mt: { xs: 0, md: 1.2 },
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            overflowX: isMobile ? 'visible' : 'auto', 
            gap: isMobile ? 2 : 3, 
            pb: 2, 
            px: { xs: 0.5, md: 3 },
            snapType: isMobile ? 'none' : 'x mandatory',
            '&::-webkit-scrollbar': { height: '8px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: `${brandColors.primary}88`, borderRadius: '4px' },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' }
          }}
        >
          {/* Card 1: Documento */}
          <Box sx={{ width: isMobile ? '100%' : { xs: '85vw', md: '450px' }, flexShrink: 0, scrollSnapAlign: 'center', display: 'flex' }}>
            <ResultBox title="Documento" sx={{ flexGrow: 1, p: 2, mb: 0 }}>
              {resultados.documento && resultados.documento.imagen ? (
                <Box display="flex" flexDirection="column" gap={2} sx={{ height: '100%' }}>
                  <Box textAlign="center" sx={{ height: isMobile ? '120px' : '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <img 
                      src={resultados.documento.imagen} 
                      alt="Documento"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'pointer' }}
                      onClick={() => setExpandedImage(resultados.documento.imagen)}
                    />
                  </Box>
                  <Box sx={{ flexGrow: 1, overflowY: isMobile ? 'visible' : 'auto' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>Datos extraídos:</Typography>
                    {renderDocumentoData(resultados.documento.datos)}
                  </Box>
                </Box>
              ) : (
                <NoDetectionMessage message="No se encontró la imagen del documento." />
              )}
            </ResultBox>
          </Box>

          {/* Card 2: Tablero */}
          <Box sx={{ width: isMobile ? '100%' : { xs: '85vw', md: '450px' }, flexShrink: 0, scrollSnapAlign: 'center', display: 'flex' }}>
            <ResultBox title="Tablero" sx={{ flexGrow: 1, p: 2, mb: 0 }}>
              {resultados.odometro ? (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box textAlign="center" sx={{ height: isMobile ? '120px' : '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <img 
                      src={formatVisualizationUrl(resultados.odometro.visualization)} 
                      alt="Tablero"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'pointer' }}
                      onClick={() => setExpandedImage(formatVisualizationUrl(resultados.odometro.visualization))}
                    />
                  </Box>
                  <Box sx={{ flexGrow: 1, overflowY: isMobile ? 'visible' : 'auto' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>Odómetro:</Typography>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {resultados.odometro.texto || 'No detectado'}
                      </Typography>
                      {resultados.odometro.texto && (
                        <IconButton onClick={() => copyToClipboard(resultados.odometro.texto, 'odometro')} size="small" sx={{ ml: 1 }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>Testigos ({resultados.odometro.testigos?.length || 0}):</Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.8}>
                      {resultados.odometro.testigos && resultados.odometro.testigos.map((testigo, index) => (
                        <Box 
                          key={index} 
                          p={0.8} px={1.2}
                          borderRadius={1} 
                          sx={{ 
                            backgroundColor: testigo.estado === 'activo' ? '#FFF4E5' : '#eef2f7',
                            border: testigo.estado === 'activo' ? '1px solid #F59E0B' : `1px solid ${brandColors.borderColor}`,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontSize: '0.78rem'
                          }}
                        >
                          {testigo.estado === 'activo' && <WarningAmberIcon sx={{ fontSize: 14, color: '#B45309' }} />}
                          <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.78rem', lineHeight: 1.2 }}>
                            {testigo.class}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <NoDetectionMessage message="No se encontraron datos del odómetro." />
              )}
            </ResultBox>
          </Box>

          {/* Card 3: Chasis */}
          <Box sx={{ width: isMobile ? '100%' : { xs: '85vw', md: '450px' }, flexShrink: 0, scrollSnapAlign: 'center', display: 'flex' }}>
            <ResultBox title="Chasis" sx={{ flexGrow: 1, p: 2, mb: 0 }}>
              {resultados.chasis ? (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box textAlign="center" sx={{ height: isMobile ? '120px' : '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    {resultados.chasis.visualization ? (
                      <img 
                        src={formatVisualizationUrl(resultados.chasis.visualization)} 
                        alt="Chasis"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'pointer' }}
                        onClick={() => setExpandedImage(formatVisualizationUrl(resultados.chasis.visualization))}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">Sin imagen</Typography>
                    )}
                  </Box>
                  <Box sx={{ flexGrow: 1, overflowY: isMobile ? 'visible' : 'auto' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>Número de Chasis:</Typography>
                    <Box display="flex" alignItems="center">
                      <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 'bold', wordBreak: 'break-all' }}>
                        {resultados.chasis.texto || 'No detectado'}
                      </Typography>
                      {resultados.chasis.texto && (
                        <IconButton onClick={() => copyToClipboard(resultados.chasis.texto, 'chasis')} size="small" sx={{ ml: 1 }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <NoDetectionMessage message="No se detectó el Chasis." />
              )}
            </ResultBox>
          </Box>
        </Box>

        {expandedImage && (
          <ImageModal 
            image={expandedImage} 
            onClose={() => setExpandedImage(null)}
          />
        )}
      </Container>
    </Fade>
  );
};

export default AnalisisGeneral;
